import React, { useState, useCallback } from 'react';
import Backbone from 'app/common/vendor/backbone';
import {
  KeepFromSourceTypes,
  CreateCardFromTemplatePopover,
} from './CreateCardFromTemplatePopover';
import { CardTemplatesPopover } from './CardTemplatesPopover';
import { forTemplate, forNamespace } from 'app/common/lib/util/i18n';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Tooltip, Popover, usePopover, PopoverScreen } from '@trello/nachos';
import PopoverConfirm from 'app/gamma/src/components/popover/popover-confirm';
import { useDeleteCardTemplateMutation } from '@trello/graphql/generated';
import styles from './CardTemplatesButton.less';

const format = forNamespace('view title');
const listFormat = forTemplate('list');
const confirmFormat = forNamespace(['confirm', 'delete template card']);

interface CardTemplatesButtonProps {
  idList: string;
  idBoard: string;
  createCard: (
    name: string,
    idCardSource: string,
    keepFromSource: KeepFromSourceTypes[],
  ) => Promise<() => void>;
}

enum Screen {
  CardTemplates,
  CreateCardFromTemplate,
  ConfirmDelete,
}

export const CardTemplatesButton: React.FunctionComponent<CardTemplatesButtonProps> = ({
  idList,
  idBoard,
  createCard,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [deletingTemplateId, setDeletingTemplateId] = useState('');
  const [deleteTemplateCard] = useDeleteCardTemplateMutation();

  const onShow = useCallback(() => {
    trackUe({
      category: Noun.CARD_TEMPLATES,
      verb: Verb.OPENS,
      directObj: Noun.CARD_TEMPLATES_MENU,
      context: {
        entry: Noun.LIST,
      },
    });
  }, []);
  const onHide = useCallback(() => {
    setIsEditing(false);
  }, []);

  const { toggle, hide, triggerRef, popoverProps, push, pop } = usePopover<
    HTMLAnchorElement
  >({
    initialScreen: Screen.CardTemplates,
    onShow,
    onHide,
  });

  const onDeleteConfirm = async () => {
    if (!deletingTemplateId) {
      return;
    }

    await deleteTemplateCard({
      variables: {
        idCard: deletingTemplateId,
      },
    });

    trackUe({
      category: Noun.CARD_TEMPLATES,
      verb: Verb.DELETES,
      directObj: Noun.CARD_TEMPLATE,
      indirectObj: Noun.CARD_TEMPLATES_MENU,
    });

    pop();
  };

  const onCreateCardFromTemplate = async (
    name: string,
    idCardSource: string,
    keepFromSource: KeepFromSourceTypes[],
  ) => {
    try {
      const fnScrollToNewCard = await createCard(
        name,
        idCardSource,
        keepFromSource,
      );
      trackUe({
        category: Noun.CARD_TEMPLATES,
        verb: Verb.COMPLETES,
        directObj: Noun.CREATE_CARD_FROM_TEMPLATE_MENU,
        context: {
          entry: Noun.LIST,
        },
      });
      hide();
      fnScrollToNewCard();
    } catch (e) {
      // `createCard` handles clean up in ListView.
    }
  };

  return (
    <>
      <Tooltip
        content={listFormat('create-from-template')}
        delay={100}
        hideTooltipOnMouseDown
      >
        <a
          className={styles.cardTemplateButton}
          ref={triggerRef}
          onClick={toggle}
          role="button"
          href="#"
        >
          <span className="icon-sm icon-template-card dark-background-hover" />
        </a>
      </Tooltip>
      <Popover {...popoverProps} dangerous_className={styles.popover}>
        <PopoverScreen
          id={Screen.CardTemplates}
          title={format('card templates')}
        >
          <CardTemplatesPopover
            idList={idList}
            idBoard={idBoard}
            onSelect={(idCardTemplate) => {
              setSelectedTemplateId(idCardTemplate);
              push(Screen.CreateCardFromTemplate);
            }}
            onTemplateCreated={(card: { shortLink: string }) => {
              hide();
              Backbone.history.navigate(`/c/${card.shortLink}`, {
                trigger: true,
              });
            }}
            confirmDelete={(templateId) => {
              setDeletingTemplateId(templateId);
              push(Screen.ConfirmDelete);
            }}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            editTemplate={() => hide()}
          />
        </PopoverScreen>
        <PopoverScreen
          id={Screen.CreateCardFromTemplate}
          title={format('create card')}
        >
          <CreateCardFromTemplatePopover
            idCard={selectedTemplateId}
            createCard={onCreateCardFromTemplate}
            editTemplate={() => {
              hide();
            }}
          />
        </PopoverScreen>
        <PopoverScreen id={Screen.ConfirmDelete} title={confirmFormat('title')}>
          <PopoverConfirm
            confirmKey="delete template card"
            confirmCallback={onDeleteConfirm}
            isDanger
          />
        </PopoverScreen>
      </Popover>
    </>
  );
};
